import { ChevronDown, Minus, Plus } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ButtonCmp from '../ButtonCmp';
import HeaderCmp from '../HeaderCmp';
import TooltipCmp from '../TooltipCmp';

interface ICardCmpProps {
    headerTitle?: React.ReactNode;
    headerRightSideContent?: React.ReactNode;
    headerLeftSideContent?: React.ReactNode;
    headerParentClassName?: string;
    isHeaderWithBg?: boolean;
    children?: React.ReactNode;
    childrenClassName?: string;
    cardClassName?: string;
    isCardContentWithoutSpacing?: boolean;
    isFooter?: boolean;
    footerRightSideContent?: React.ReactNode;
    footerLeftSideContent?: React.ReactNode;
    footerParentClassName?: string;
    isCardFooterWithoutSpacing?: boolean;
    isAccordion?: boolean;
    accordionIconType?: 'chevron' | 'plus-minus';
    specificSectionAccordion?: boolean;
    defaultOpen?: boolean;
    hideContentOnCollapse?: boolean;
    onAccordionToggle?: (isOpen: boolean) => void;
    scrollToOnOpen?: boolean;
}

const CardCmp = ({
    headerTitle,
    headerRightSideContent,
    headerLeftSideContent,
    headerParentClassName = '',
    isHeaderWithBg = false,
    children,
    childrenClassName = '',
    cardClassName = '',
    isCardContentWithoutSpacing = false,
    isFooter = false,
    footerRightSideContent,
    footerLeftSideContent,
    footerParentClassName = '',
    isCardFooterWithoutSpacing = false,
    isAccordion = false,
    accordionIconType = 'chevron',
    specificSectionAccordion = false,
    defaultOpen = true,
    hideContentOnCollapse = true,
    onAccordionToggle,
    scrollToOnOpen = false,
}: ICardCmpProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const accordionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen && scrollToOnOpen && accordionRef.current) {
            const id = setTimeout(() => {
                accordionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 0);

            return () => clearTimeout(id);
        }
    }, [isOpen, scrollToOnOpen]);

    const content = (
        <>
            <div
                className={`${isCardContentWithoutSpacing ? '' : 'px-3'} ${childrenClassName} ${
                    isAccordion && specificSectionAccordion
                        ? `[&_.thisSectionAccordion]:grid [&_.thisSectionAccordion]:[transition:grid-template-rows_300ms_ease] ${
                              !isOpen ? '[&_.thisSectionAccordion]:[grid-template-rows:0fr]' : '[&_.thisSectionAccordion]:[grid-template-rows:1fr]'
                          }`
                        : ''
                }`}
            >
                {children}
            </div>
            {isFooter && (footerLeftSideContent || footerRightSideContent) && (
                <div
                    className={`${isCardFooterWithoutSpacing ? '' : 'px-3 py-3'}  border-t border-gray100 flex items-center ${
                        (footerLeftSideContent && footerRightSideContent) || footerLeftSideContent ? 'justify-between' : 'justify-end'
                    } gap-x-1.5 ${footerParentClassName}`}
                >
                    {footerLeftSideContent ? footerLeftSideContent : <></>}
                    {footerRightSideContent ? footerRightSideContent : <></>}
                </div>
            )}
        </>
    );

    return (
        <div className={`table-box-wrap ${cardClassName}`}>
            {isAccordion ? (
                <div
                    ref={accordionRef}
                    className={`cursor-pointer ${isOpen ? 'rounded-t-[inherit]' : 'rounded-[inherit]'}`}
                    onClick={() => {
                        const newState = !isOpen;
                        setIsOpen(newState);
                        onAccordionToggle?.(newState);
                    }}
                >
                    <HeaderCmp
                        title={headerTitle}
                        rightSideContent={
                            <div className="flex items-center gap-2 [&>*:not(:first-child)]:pl-2 divide-x divide-borderPrimary">
                                {headerRightSideContent && <div onClick={(e) => e.stopPropagation()}>{headerRightSideContent}</div>}
                                <div>
                                    <TooltipCmp message={isOpen ? t('Common.Collapse') : t('Common.Expand')} childrenClassName="!flex">
                                        <ButtonCmp
                                            className={`flex-none btn-link-secondary !shadow-none`}
                                            size="none"
                                            disabled={false}
                                            loading={false}
                                            onClick={() => {}}
                                            icon={
                                                accordionIconType === 'plus-minus' ? (
                                                    isOpen ? (
                                                        <Minus className={`w-4 h-4 text-grayLight600 transition-transform duration-300`} />
                                                    ) : (
                                                        <Plus className={`w-4 h-4 text-grayLight600 transition-transform duration-300`} />
                                                    )
                                                ) : (
                                                    <ChevronDown className={`w-4 h-4 text-grayLight600 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'} `} />
                                                )
                                            }
                                        >
                                            <></>
                                        </ButtonCmp>
                                    </TooltipCmp>
                                </div>
                            </div>
                        }
                        leftSideContent={headerLeftSideContent ? <div onClick={(e) => e.stopPropagation()}>{headerLeftSideContent}</div> : undefined}
                        parentClassName={`${headerParentClassName} ${!isOpen && !specificSectionAccordion && hideContentOnCollapse ? 'rounded-b-[inherit] !border-b-0' : ''}`}
                        isHeaderWithBg={isHeaderWithBg}
                    />
                </div>
            ) : (
                <HeaderCmp
                    title={headerTitle}
                    rightSideContent={headerRightSideContent}
                    leftSideContent={headerLeftSideContent}
                    parentClassName={headerParentClassName}
                    isHeaderWithBg={isHeaderWithBg}
                />
            )}
            {isAccordion && !specificSectionAccordion && hideContentOnCollapse ? (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateRows: isOpen ? '1fr' : '0fr',
                        transition: 'grid-template-rows 300ms ease',
                    }}
                >
                    <div className="overflow-hidden">{content}</div>
                </div>
            ) : (
                content
            )}
        </div>
    );
};

export default CardCmp;
